import * as Sentry from '@sentry/react';
import { useAppStore } from '../../store';
import {
  ErrorType,
  AppError,
  AuthError,
  NetworkError,
  FirebaseError,
  ValidationError,
  PermissionError,
} from './types';

/**
 * Categorize an error and convert it to an AppError
 */
export function categorizeError(error: unknown): AppError {
  if (error instanceof AuthError ||
      error instanceof NetworkError ||
      error instanceof FirebaseError ||
      error instanceof ValidationError ||
      error instanceof PermissionError) {
    return error;
  }

  // Handle Firebase errors
  if (error && typeof error === 'object' && 'code' in error) {
    const firebaseError = error as { code: string; message?: string };
    
    if (firebaseError.code === 'permission-denied') {
      return new PermissionError(
        firebaseError.message || 'You do not have permission to perform this action',
        firebaseError.code
      );
    }
    
    if (firebaseError.code === 'unauthenticated' || firebaseError.code === 'auth/') {
      return new AuthError(
        firebaseError.message || 'You must be logged in to perform this action',
        firebaseError.code
      );
    }
    
    if (firebaseError.code.startsWith('firestore/') || 
        firebaseError.code.startsWith('storage/') ||
        firebaseError.code.startsWith('functions/')) {
      return new FirebaseError(
        firebaseError.message || 'A database error occurred',
        firebaseError.code
      );
    }
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new NetworkError('Network request failed. Please check your connection.');
  }

  // Handle validation errors
  if (error instanceof Error && error.name === 'ValidationError') {
    return new ValidationError(error.message);
  }

  // Default to unknown error
  return {
    type: ErrorType.UNKNOWN_ERROR,
    message: error instanceof Error ? error.message : 'An unexpected error occurred',
    originalError: error instanceof Error ? error : undefined,
  };
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error: AppError): string {
  switch (error.type) {
    case ErrorType.AUTH_ERROR:
      return 'Please log in to continue';
    case ErrorType.PERMISSION_ERROR:
      return 'You do not have permission to perform this action';
    case ErrorType.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again';
    case ErrorType.VALIDATION_ERROR:
      return error.message || 'Please check your input and try again';
    case ErrorType.FIREBASE_ERROR:
      if (error.code === 'unavailable') {
        return 'Service temporarily unavailable. Please try again later';
      }
      return error.message || 'An error occurred. Please try again';
    default:
      return error.message || 'An unexpected error occurred';
  }
}

/**
 * Handle error and show notification
 */
export function handleError(error: unknown, showNotification = true): AppError {
  const appError = categorizeError(error);
  const message = getUserFriendlyMessage(appError);

  // Log error to console
  console.error('Error handled:', appError);

  // Show notification if requested
  if (showNotification) {
    const { addNotification } = useAppStore.getState();
    addNotification(message, 'error');
  }

  // Send to Sentry
  if (appError.originalError) {
    Sentry.captureException(appError.originalError, {
      tags: {
        errorType: appError.type,
        errorCode: appError.code,
      },
      extra: appError.details,
    });
  }

  return appError;
}

/**
 * Wrap async function with error handling
 */
export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  showNotification = true
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, showNotification);
      throw error; // Re-throw to allow caller to handle if needed
    }
  }) as T;
}
